import {
  createAsyncThunk,
  createSlice
} from '@reduxjs/toolkit'
import fetch from 'isomorphic-unfetch'
import absoluteApiUrl from './absoluteApiUrl'
import env from '@polleverywhere/core/lib/env'
import Job from '@polleverywhere/core/lib/models/job'

const sliceName = 'participantResponseHistory'
const thunks = {}

thunks.fetchResponses = createAsyncThunk(
  `${sliceName}/fetchResponses`,
  async (page = 1, { getState }) => {
    const state = getState()[sliceName]
    const data = await fetch(absoluteApiUrl({
      path: '/api/results/history',
      search: { page: page.toString(), presenter: state.presenter }
    }), {
      credentials: 'include'
    })
    const result = await data.json()
    const responses = result.data
    return { responses, meta: result.meta }
  }
)

thunks.focusResponse = createAsyncThunk(
  `${sliceName}/focusResponse`,
  async (responseId) => {
    const data = await fetch(absoluteApiUrl({
      path: `/api/results/${responseId}`
    }), {
      credentials: 'include'
    })
    const json = await data.json()
    if (data.status === 200) {
      return json
    } else {
      throw new Error(json.error?.message || 'Could not load response')
    }
  }
)

thunks.exportResponses = createAsyncThunk(
  `${sliceName}/exportResponses`,
  async () => {
    return await Job.create(env.get('api_url').segment('api/results/history_export')).promise
  }
)

thunks.filterPresenter = createAsyncThunk(
  `${sliceName}/filterPresenter`,
  async (username = '', { dispatch }) => {
    dispatch({ type: `${sliceName}/filterPresenterUpdated`, payload: username })
    dispatch(thunks.fetchResponses())
  }
)

export const LoadingState = {
  done: 'done',
  pending: 'pending',
  error: 'error'
}

export const ExportStatus = {
  initial: 'initial',
  done: 'done',
  pending: 'pending',
  error: 'error'
}

const initialState = {
  loading: LoadingState.pending,
  exportStatus: ExportStatus.initial,
  currentRequestId: undefined,
  error: undefined,
  presenter: '',
  meta: {
    per_page: 0,
    current_page: 0,
    total_pages: 0,
    total_count: 0
  },
  responses: [],
  focus: undefined
}

const slice = createSlice({
  name: sliceName,
  initialState: initialState,
  reducers: {
    focusCleared (state) {
      state.focus = undefined
    },
    filterPresenterUpdated (state, action) {
      state.focus = undefined
      state.presenter = action.payload
    },
    mockState (_, action) {
      // Only used for stories/testing
      return { ...initialState, ...action.payload }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(thunks.fetchResponses.fulfilled, (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return

      state.loading = LoadingState.done
      state.responses = action.payload.responses
      state.meta = action.payload.meta
      state.currentRequestId = undefined
    })

    builder.addCase(thunks.focusResponse.fulfilled, (state, action) => {
      console.log('FOCUS COMPLETE')
      if (state.currentRequestId !== action.meta.requestId) return

      state.loading = LoadingState.done
      state.focus = action.payload
      state.currentRequestId = undefined
    })

    builder.addCase(thunks.exportResponses.pending, (state) => {
      state.exportStatus = ExportStatus.pending
    })
    builder.addCase(thunks.exportResponses.fulfilled, (state) => {
      state.exportStatus = ExportStatus.done
    })
    builder.addCase(thunks.exportResponses.rejected, (state) => {
      state.exportStatus = ExportStatus.error
    })

    builder.addDefaultCase((state, action) => {
      if (action.type.endsWith('pending')) {
        state.loading = LoadingState.pending
        state.currentRequestId = action.meta.requestId
      } else if (action.type.endsWith('rejected')) {
        if (state.currentRequestId !== action.meta.requestId) return

        state.loading = LoadingState.error
        state.error = action.error.message
        state.currentRequestId = undefined
      }
    })
  }
})

const sliceExport = {
  name: sliceName,
  reducer: slice.reducer,
  ...slice.actions,
  ...thunks
}

export { sliceExport as slice }
